import { ChatBotUIProduct } from "@jugl-web/domain-resources/chat-bot/types";
import { getProductCoverPriceText } from "@jugl-web/domain-resources/chat-bot/utils/getProductCoverPriceText";
import { PlainButton } from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils";
import { Portal } from "@jugl-web/utils/utils/Portal";
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ProductCard } from "../../../ProductCard";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

interface ChatBotProductListProps {
  products: ChatBotUIProduct[];
  navigationSlotElement: HTMLElement | null;
  className?: string;
  onSelectProduct: (product: ChatBotUIProduct) => void;
}

const PRODUCT_CARD_WIDTH = 250;

export const ChatBotProductList: FC<ChatBotProductListProps> = ({
  products,
  navigationSlotElement,
  className,
  onSelectProduct,
}) => {
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = scrollContainerRef.current;

    if (!element) {
      return undefined;
    }

    const updateScrollState = () => {
      setCanScrollPrevious(element.scrollLeft > 0);
      setCanScrollNext(
        element.scrollLeft + element.clientWidth < element.scrollWidth - 1
      );
    };

    updateScrollState();

    element.addEventListener("scroll", updateScrollState, { passive: true });

    return () => {
      element.removeEventListener("scroll", updateScrollState);
    };
  }, []);

  const scrollPrevious = useCallback(() => {
    scrollContainerRef.current?.scrollBy({
      left: -PRODUCT_CARD_WIDTH,
      behavior: "smooth",
    });
  }, []);

  const scrollNext = useCallback(() => {
    scrollContainerRef.current?.scrollBy({
      left: PRODUCT_CARD_WIDTH,
      behavior: "smooth",
    });
  }, []);

  return (
    <div
      ref={scrollContainerRef}
      className={cx(
        "jugl__hidden-scrollbar relative -left-3 flex w-[calc(100%_+_24px)] snap-x snap-proximity scroll-px-3 items-stretch gap-3 overflow-x-auto px-3",
        className
      )}
    >
      {products.map((product) => (
        <ProductCard
          key={product.id}
          name={product.title}
          imageUrl={product.coverImageUrl}
          priceText={getProductCoverPriceText(
            product.priceSummary,
            product.currencyCode
          )}
          className="shrink-0 snap-start"
          style={{ width: PRODUCT_CARD_WIDTH }}
          onSelect={() => onSelectProduct(product)}
        />
      ))}
      {navigationSlotElement && (
        <Portal container={navigationSlotElement}>
          <div className="flex items-center gap-6">
            <ArrowButton
              isDisabled={!canScrollPrevious}
              onClick={scrollPrevious}
            >
              <ArrowLeftIcon />
            </ArrowButton>
            <ArrowButton isDisabled={!canScrollNext} onClick={scrollNext}>
              <ArrowRightIcon />
            </ArrowButton>
          </div>
        </Portal>
      )}
    </div>
  );
};

interface ArrowButtonProps {
  children: ReactNode;
  isDisabled: boolean;
  onClick: () => void;
}

const ArrowButton: FC<ArrowButtonProps> = ({
  children,
  isDisabled,
  onClick,
}) => {
  return (
    <PlainButton
      disabled={isDisabled}
      onClick={onClick}
      className={cx(
        "jugl__focusable-outline flex h-6 w-8 shrink-0 items-center justify-center rounded-md",
        "cursor-pointer hover:bg-grey-100 active:bg-grey-200",
        "disabled:cursor-default disabled:text-[#E0E2E3] disabled:hover:bg-transparent"
      )}
    >
      {children}
    </PlainButton>
  );
};
